import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { MdVideoCall } from 'react-icons/md';
import { FiExternalLink } from 'react-icons/fi';
import Modal from '../ui/Modal';
import { Button, Select } from '../ui/Kit';
import { AppointmentTypeBadge, AppointmentStatusBadge } from './appointmentBadges';
import {
  APPOINTMENT_TYPE_LABELS,
  APPOINTMENT_STATUSES,
  formatTimeRange,
  leadDisplayName,
  deleteGoogleEventsForAppointment,
} from '../../lib/calendarUtils';
import { api } from '../../lib/api';
import { useToast } from '../ui/Toast';

const STATUS_OPTIONS = APPOINTMENT_STATUSES.filter((o) => o.value).map((o) => ({
  value: o.value,
  label: o.label,
}));

export default function AppointmentDetailModal({
  appointment,
  isOpen,
  onClose,
  onReschedule,
  onUpdated,
}) {
  const { addToast } = useToast();
  const [status, setStatus] = useState(appointment?.status || 'scheduled');
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (appointment?.status) setStatus(appointment.status);
  }, [appointment?.id, appointment?.status]);

  if (!appointment) return null;

  const leadName = leadDisplayName(appointment);
  const assigneeName = appointment.assignee?.full_name || appointment.assignee?.email || '—';
  const when = appointment.scheduled_at
    ? format(parseISO(appointment.scheduled_at), "EEEE, MMMM d, yyyy 'at' h:mm a")
    : '—';
  const typeLabel = APPOINTMENT_TYPE_LABELS[appointment.appointment_type] || appointment.appointment_type;

  const handleStatusChange = async (e) => {
    const next = e.target.value;
    setStatus(next);
    setSaving(true);
    try {
      await api.appointments.updateStatus(appointment.id, next);
      addToast({ type: 'success', title: 'Status updated' });
      onUpdated?.();
    } catch (err) {
      setStatus(appointment.status);
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await api.appointments.updateStatus(appointment.id, 'completed');
      addToast({ type: 'success', title: 'Appointment marked complete' });
      onUpdated?.();
      onClose();
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setSaving(true);
    try {
      await deleteGoogleEventsForAppointment(appointment);
      await api.appointments.updateStatus(appointment.id, 'cancelled');
      addToast({ type: 'success', title: 'Appointment cancelled' });
      setConfirmCancel(false);
      onUpdated?.();
      onClose();
    } catch (err) {
      addToast({ type: 'error', title: 'Cancel failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={confirmCancel}
        onClose={() => !saving && setConfirmCancel(false)}
        title="Cancel appointment?"
        className="max-w-sm"
      >
        <p className="text-sm text-gray-600 mb-4">
          Mark this appointment as cancelled? If synced, it will be removed from Google Calendar.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirmCancel(false)} disabled={saving}>
            Keep
          </Button>
          <Button variant="danger" onClick={handleCancel} disabled={saving}>
            {saving ? 'Cancelling…' : 'Cancel appointment'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose} title="Appointment details" className="max-w-lg">
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{appointment.title || leadName}</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <AppointmentTypeBadge type={appointment.appointment_type} />
              <AppointmentStatusBadge status={appointment.status} />
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-3">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Type</dt>
              <dd className="text-gray-900 mt-0.5">{typeLabel}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Lead</dt>
              <dd className="mt-0.5">
                {appointment.lead_id ? (
                  <Link to={`/leads/${appointment.lead_id}`} className="text-primary font-medium hover:underline">
                    {leadName}
                  </Link>
                ) : (
                  <span className="text-gray-900">{leadName}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Assigned agent</dt>
              <dd className="text-gray-900 mt-0.5">{assigneeName}</dd>
            </div>
            {Array.isArray(appointment.guests) && appointment.guests.length > 0 && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Guests</dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {appointment.guests.map((guest, index) => (
                    <span
                      key={`${guest.type}-${guest.user_id || guest.name}-${index}`}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200"
                    >
                      {guest.name}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">When</dt>
              <dd className="text-gray-900 mt-0.5">{when}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Duration</dt>
              <dd className="text-gray-900 mt-0.5">{appointment.duration_minutes || 30} minutes</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Time range</dt>
              <dd className="text-gray-900 mt-0.5">{formatTimeRange(appointment)}</dd>
            </div>
            {appointment.notes && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Notes</dt>
                <dd className="text-gray-700 mt-0.5 whitespace-pre-wrap">{appointment.notes}</dd>
              </div>
            )}
            {appointment.zoom_join_url && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1.5">
                  <MdVideoCall className="w-3.5 h-3.5" aria-hidden />
                  Zoom Meeting
                </dt>
                <dd className="mt-0.5">
                  <a
                    href={appointment.zoom_join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#00AAFF] font-medium hover:underline"
                  >
                    Join Zoom Meeting
                    <FiExternalLink className="w-3.5 h-3.5" aria-hidden />
                  </a>
                </dd>
              </div>
            )}
          </dl>

          <Select
            label="Status"
            value={status}
            onChange={handleStatusChange}
            disabled={saving}
            options={STATUS_OPTIONS}
          />

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={() => onReschedule?.(appointment)} disabled={saving}>
              Reschedule
            </Button>
            <Button variant="primary" onClick={handleComplete} disabled={saving || appointment.status === 'completed'}>
              Mark Complete
            </Button>
            <Button variant="danger" onClick={() => setConfirmCancel(true)} disabled={saving || appointment.status === 'cancelled'}>
              Cancel Appointment
            </Button>
            <Button variant="ghost" className="ml-auto" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
